import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { History, MapPin, Clock, Lock } from "lucide-react";
import { useMatch } from "@/hooks/useMatch";
import { useAuth } from "@/context/AuthContext";
import { Pitch, Bench } from "@/components/Pitch";
import { PlayerSheet } from "@/components/PlayerSheet";
import { DraftBoard } from "@/components/DraftBoard";
import { PlayerChip } from "@/components/PlayerChip";
import { ScoreBoard } from "@/components/ScoreBoard";
import { CaptainPicker } from "@/components/CaptainPicker";
import { Guests } from "@/components/Guests";
import { fmtDate } from "@/lib/football";

const TABS = [["joueurs", "Joueurs"], ["draft", "Draft"], ["terrain", "Terrain"]];

const PlayersTab = ({ data, act }) => {
  const confirmed = data.players.filter((p) => p.registration_status === "confirmed");
  const waiting = data.players.filter((p) => p.registration_status === "waiting_list").sort((a, b) => a.waiting_list_position - b.waiting_list_position);
  return (
    <div className="space-y-5">
      <CaptainPicker data={data} act={act} />
      <Guests data={data} act={act} />
      <div>
        <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2" data-testid="confirmed-title">Inscrits · {confirmed.length} / {data.capacity}</p>
        {confirmed.length === 0 && <p className="text-sm text-slate-500">Aucun joueur inscrit pour l'instant.</p>}
        <div className="grid grid-cols-2 gap-2">
          {confirmed.map((p) => <PlayerChip key={p.id} p={p} testid={`confirmed-player-${p.id}`} />)}
        </div>
      </div>
      {waiting.length > 0 && (
        <div data-testid="waiting-list">
          <p className="text-xs font-bold text-amber-600 uppercase tracking-wider mb-2">Liste d'attente <span className="normal-case font-medium text-slate-500">· priorité aux plus anciens</span></p>
          <div className="space-y-2">
            {waiting.map((p, i) => (
              <div key={p.id} className="flex items-center gap-2">
                <span className="num w-6 text-sm font-bold text-slate-500">{i + 1}.</span>
                <div className="flex-1"><PlayerChip p={p} testid={`waiting-player-${p.id}`} /></div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default function Matches() {
  const { user } = useAuth();
  const { data, act } = useMatch("current", 8000);
  const [tab, setTab] = useState(null);
  const [sel, setSel] = useState(null);

  useEffect(() => {
    if (data && !tab) setTab(data.match.lineup_generated ? "terrain" : data.draft.started ? "draft" : "joueurs");
  }, [data, tab]);

  if (!data) return <div className="h-96 rounded-3xl bg-white animate-pulse" />;
  const m = data.match;
  const selPlayer = sel && data.players.find((p) => p.id === sel);

  return (
    <div data-testid="matches-page" className="space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[11px] font-black tracking-[0.2em] text-cyan-600">BLANC VS NOIR</p>
          <h1 className="font-display text-2xl font-extrabold">{fmtDate(m.date)}</h1>
          <p className="text-xs text-slate-500 flex gap-3 mt-0.5"><span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{m.time}</span><span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{m.location}</span></p>
        </div>
        <Link to="/matchs/historique" data-testid="history-link" className="flex items-center gap-1.5 rounded-full bg-white border border-slate-200 shadow-sm px-3 py-2 text-xs font-semibold">
          <History className="w-4 h-4" /> Historique
        </Link>
      </div>
      <div className="grid grid-cols-3 gap-1 rounded-2xl bg-white p-1 border border-slate-200 shadow-sm" data-testid="match-tabs">
        {TABS.map(([k, l]) => (
          <button key={k} data-testid={`match-tab-${k}`} onClick={() => setTab(k)}
            className={`rounded-xl py-2.5 text-sm font-bold transition-colors ${tab === k ? "bg-cyan-400 text-zinc-950 shadow" : "text-slate-500"}`}>{l}</button>
        ))}
      </div>
      <ScoreBoard data={data} />
      {m.teams_locked && <p data-testid="teams-locked-badge" className="text-xs text-slate-500 flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" /> Équipes verrouillées</p>}
      {tab === "joueurs" && <PlayersTab data={data} act={act} />}
      {tab === "draft" && <DraftBoard data={data} act={act} onDone={() => setTab("terrain")} onTap={(p) => setSel(p.id)} />}
      {tab === "terrain" && (
        <div className="space-y-3">
          {!m.lineup_generated && <p className="text-sm text-slate-500 text-center">Le terrain se remplit en direct pendant le draft.</p>}
          <Pitch data={data} onTap={(p) => setSel(p.id)} selectedId={sel} />
          <p className="text-[11px] text-slate-500 text-center">Touchez un joueur pour ajouter ses buts et passes décisives.</p>
          <Bench data={data} onTap={(p) => setSel(p.id)} />
        </div>
      )}
      {user.role === "admin" && <Link to="/admin" data-testid="matches-admin-link" className="block text-center text-xs text-cyan-600 underline pt-2">Gérer ce match (Administration)</Link>}
      <PlayerSheet data={data} player={selPlayer} onClose={() => setSel(null)} act={act} />
    </div>
  );
}
